import { Button, DatePicker, Flex, Input, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DATE_PICKER_FORMAT } from '@app/constants';

import type { ContentType } from '@pages/Clients/NewClientsList/components/MultiFilter/types';

import { useNewClientsListContext } from '@pages/Clients/NewClientsList/context/useNewClientsListContext';
import { PF } from '@pages/Clients/NewClientsList/data/constants';
import { FilterWrapper } from '@pages/Clients/NewClientsList/ui/FilterWrapper';

import type { NoUndefinedRangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type DayjsRange = NoUndefinedRangeValueType<dayjs.Dayjs>;

interface QuickButton {
  label: string;
  from: dayjs.Dayjs;
}

const QUICK_BUTTONS: QuickButton[] = [
  { label: 'Сегодня', from: dayjs() },
  { label: 'Вчера', from: dayjs().subtract(1, 'day') },
  { label: 'Последняя неделя', from: dayjs().subtract(7, 'day') },
  { label: 'Последний месяц', from: dayjs().subtract(1, 'month') },
  { label: 'Последние 3 месяца', from: dayjs().subtract(3, 'month') },
];

const MIN_LAST_DAYS = 1;

export const PeriodFilter: ContentType = ({ open, onClose, id }) => {
  const { filtersMap, onChangePeriod } = useNewClientsListContext();

  const period = filtersMap.get(id)?.period ?? null;

  const [localPeriod, setLocalPeriod] = useState<DayjsRange | null>(null);
  const [localLastDays, setLocalLastDays] = useState<number | undefined>(
    undefined
  );

  const onConfirm = useCallback(() => {
    onChangePeriod(localPeriod, id);
    onClose?.();
  }, [id, localPeriod, onChangePeriod, onClose]);

  useEffect(() => {
    if (!open) {
      setLocalPeriod(null);
      setLocalLastDays(undefined);
      return;
    }

    setLocalPeriod(period);
    setLocalLastDays(undefined);
  }, [open, period]);

  useEffect(() => {
    if (!open) return;
    if (localLastDays === undefined) return;

    setLocalPeriod([dayjs().subtract(localLastDays, 'day'), dayjs()]);
  }, [localLastDays, open]);

  const rangePickerValue = useMemo(() => localPeriod, [localPeriod]);

  // ====== NEW: запрет отрицательных и “грязного” ввода ======
  const parsePositiveIntOrUndefined = useCallback((raw: string) => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    if (digitsOnly.length === 0) return undefined;

    const parsed = Number(digitsOnly);
    if (!Number.isFinite(parsed)) return undefined;

    const floored = Math.floor(parsed);
    return floored < MIN_LAST_DAYS ? MIN_LAST_DAYS : floored;
  }, []);

  const handleLastDaysKeyDown = useCallback<
    NonNullable<React.ComponentProps<typeof Input>['onKeyDown']>
  >((e) => {
    if (
      e.key === '-' ||
      e.key === '+' ||
      e.key === 'e' ||
      e.key === 'E' ||
      e.key === '.' ||
      e.key === ','
    ) {
      e.preventDefault();
    }
  }, []);

  const handleLastDaysChange = useCallback<
    NonNullable<React.ComponentProps<typeof Input>['onChange']>
  >(
    (e) => {
      const next = parsePositiveIntOrUndefined(e.target.value);

      if (next === undefined) {
        setLocalLastDays(undefined);
        return;
      }

      setLocalLastDays(next);
    },
    [parsePositiveIntOrUndefined]
  );

  const lastDaysInputValue = useMemo(() => {
    return localLastDays === undefined ? '' : String(localLastDays);
  }, [localLastDays]);
  // =========================================================

  return (
    <FilterWrapper onConfirm={onConfirm}>
      <Flex vertical gap={8}>
        <RangePicker
          format={DATE_PICKER_FORMAT[0]}
          allowEmpty={[true, true]}
          placeholder={['Дата с', 'Дата по']}
          value={rangePickerValue}
          onChange={(dates) => {
            setLocalLastDays(undefined);
            setLocalPeriod(dates);
          }}
          className={`${PF}__range-picker`}
        />

        <Flex gap={4} vertical>
          {QUICK_BUTTONS.map(({ label, from }) => {
            const isSelected =
              localPeriod != null &&
              localPeriod[0]?.isSame(from, 'day') &&
              localPeriod[1]?.isSame(dayjs(), 'day');

            return (
              <Button
                key={label}
                size="small"
                type={isSelected ? 'primary' : 'text'}
                onClick={() => {
                  setLocalLastDays(undefined);
                  setLocalPeriod([from, dayjs()]);
                }}
              >
                {label}
              </Button>
            );
          })}

          <Flex gap={8} align="center">
            <Text>Последние</Text>
            <Input
              size="small"
              style={{ width: 100 }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={lastDaysInputValue}
              onKeyDown={handleLastDaysKeyDown}
              onChange={handleLastDaysChange}
            />
            <Text>дней</Text>
          </Flex>
        </Flex>
      </Flex>
    </FilterWrapper>
  );
};
